import { useId } from 'react';
import type { Course, Cuisine } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { placeholderStyle, type PlaceholderGlyph } from '../../lib/recipeVisuals';

/** Course glyphs in the brand outline style (48px grid). */
const GLYPHS: Record<PlaceholderGlyph, string[]> = {
  pot: ['M10 20h28v12a6 6 0 0 1-6 6H16a6 6 0 0 1-6-6V20Z', 'M6 20h36', 'M24 8v6', 'M20 14h8'],
  bowl: ['M7 22h34a17 17 0 0 1-34 0Z', 'M18 8c-2 3 2 5 0 8', 'M26 8c-2 3 2 5 0 8', 'M17 39h14'],
  leaf: ['M10 38C10 20 22 9 40 9c0 18-11 30-29 30Z', 'M10 38 28 20'],
  bread: [
    'M8 22a8 8 0 0 1 8-12h16a8 8 0 0 1 8 12v14a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V22Z',
    'M18 18l-3 6',
    'M26 18l-3 6',
    'M34 18l-3 6',
  ],
  cup: [
    'M12 14h22v14a10 10 0 0 1-10 10h-2a10 10 0 0 1-10-10V14Z',
    'M34 18h3a5 5 0 0 1 0 10h-3',
    'M18 6v4',
    'M24 6v4',
  ],
  cake: [
    'M9 24h30v14H9z',
    'M9 31c4 0 4-3 7.5-3s3.5 3 7.5 3 4-3 7.5-3 3.5 3 7.5 3',
    'M24 24v-8',
    'M24 9c-2 2-2 4 0 5 2-1 2-3 0-5Z',
  ],
  egg: [
    'M24 8c7 0 13 11 13 19a13 13 0 0 1-26 0c0-8 6-19 13-19Z',
    'M24 23a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z',
  ],
  drop: ['M24 7c6 9 12 15 12 22a12 12 0 0 1-24 0c0-7 6-13 12-22Z', 'M18 30a6 6 0 0 0 6 6'],
  skewer: ['M8 40 40 8', 'M14 26l8 8', 'M20 20l8 8', 'M26 14l8 8'],
};

/**
 * Generated stand-in for a recipe with no photo: a brand gradient with a
 * pattern and a course glyph, chosen deterministically from the recipe's
 * course / cuisine / title — never a grey box.
 */
export function RecipePlaceholder({
  seed,
  courses,
  cuisines,
  className = '',
  shape = 'card',
}: {
  seed: string;
  courses?: readonly Course[] | undefined;
  cuisines?: readonly Cuisine[] | undefined;
  className?: string;
  /** `banner` keeps the glyph small and high on a wide hero image. */
  shape?: 'card' | 'banner';
}) {
  const banner = shape === 'banner';
  const w = banner ? 480 : 160;
  const h = banner ? 160 : 120;
  const cx = w / 2;
  const cy = banner ? 52 : 60;
  const uid = useId().replace(/:/g, '');
  const { palette: p, pattern, glyph } = placeholderStyle(seed, courses, cuisines);
  return (
    <div aria-hidden className={`overflow-hidden ${className}`}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="xMidYMid slice"
        className="block size-full"
        focusable="false"
      >
        <defs>
          <linearGradient id={`g${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={p.from} />
            <stop offset="1" stopColor={p.to} />
          </linearGradient>
          <pattern id={`p${uid}`} width="16" height="16" patternUnits="userSpaceOnUse">
            {pattern === 'dots' ? (
              <circle cx="4" cy="4" r="1.6" fill={p.pattern} opacity="0.35" />
            ) : pattern === 'stripes' ? (
              <path
                d="M-4 4 4-4M0 16 16 0M12 20 20 12"
                stroke={p.pattern}
                strokeWidth="2"
                opacity="0.18"
              />
            ) : (
              <circle
                cx="8"
                cy="8"
                r="5"
                fill="none"
                stroke={p.pattern}
                strokeWidth="1.2"
                opacity="0.25"
              />
            )}
          </pattern>
        </defs>
        <rect width={w} height={h} fill={`url(#g${uid})`} />
        <rect width={w} height={h} fill={`url(#p${uid})`} />
        <circle cx={cx} cy={cy} r={banner ? 26 : 30} fill={p.disc} opacity="0.9" />
        <g
          transform={`translate(${cx - (banner ? 15 : 18)} ${cy - (banner ? 15 : 18)}) scale(${banner ? 0.625 : 0.75})`}
          fill="none"
          stroke={p.icon}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {GLYPHS[glyph].map((d) => (
            <path key={d} d={d} />
          ))}
        </g>
      </svg>
    </div>
  );
}

export function RecipePhoto({
  photoMediaId,
  title,
  variant = 'thumb',
  className = '',
  seed,
  courses,
  cuisines,
  shape,
}: {
  photoMediaId: string | null;
  title: string;
  variant?: 'thumb' | 'full';
  className?: string;
  /** Placeholder seed; defaults to the title. */
  seed?: string;
  courses?: readonly Course[] | undefined;
  cuisines?: readonly Cuisine[] | undefined;
  shape?: 'card' | 'banner';
}) {
  if (!photoMediaId) {
    return (
      <RecipePlaceholder
        seed={seed ?? title}
        courses={courses}
        cuisines={cuisines}
        className={className}
        {...(shape ? { shape } : {})}
      />
    );
  }
  return (
    <img
      src={
        variant === 'thumb' ? api.media.thumbUrlFor(photoMediaId) : api.media.urlFor(photoMediaId)
      }
      alt={`Photo of ${title}`}
      loading="lazy"
      decoding="async"
      className={`object-cover ${className}`}
    />
  );
}
